import { editableSectionNames, propertyNames, sectionsToProperties } from './constants'

const RESORT_DISCOVERY_SECTION_PROPERTIES = {
  header: ['headerBgColor', 'logoUrl', 'logoWidth', 'logoHeight'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor', 'ctaTextColor'],
  lifestyle: ['lifestyleImage', 'lifestyleImageLinkUrl', 'lifestyleImageAlt'],
  discoverIntro: ['galleryTitle', 'discoverIntroText'],
  discoverButtons: [
    'discoverBtn1',
    'discoverBtn1LinkUrl',
    'discoverBtn2',
    'discoverBtn2LinkUrl',
    'discoverBtn3',
    'discoverBtn3LinkUrl',
    'discoverBtn4',
    'discoverBtn4LinkUrl'
  ],
  gallery: [
    'gallery1Image',
    'gallery1ImageLinkUrl',
    'gallery2Image',
    'gallery2ImageLinkUrl',
    'gallery3Image',
    'gallery3ImageLinkUrl',
    'gallery4Image',
    'gallery4ImageLinkUrl'
  ],
  footer: ['socialMediaLinks', 'hotelPhone', 'hotelWebsite', 'hotelWebsiteUrl', 'finePrintHtml']
}

const EDITORIAL_EVENT_SECTION_PROPERTIES = {
  header: ['headerBgColor', 'logoUrl', 'logoWidth', 'logoHeight', 'headerTaglineScript', 'headerTaglineSans'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor', 'ctaTextColor'],
  experiencesIntro: ['featuresSectionTitle'],
  col1: ['col1Image', 'col1ImageLinkUrl', 'col1Html', 'col1LinkText', 'col1LinkUrl'],
  col2: ['col2Image', 'col2ImageLinkUrl', 'col2Html', 'col2LinkText', 'col2LinkUrl'],
  col3: ['col3Image', 'col3ImageLinkUrl', 'col3Html', 'col3LinkText', 'col3LinkUrl'],
  footer: ['socialMediaLinks', 'footerAccentColor', 'finePrintHtml', 'hotelPhone']
}

const CINEMATIC_CONFIRM_SECTION_PROPERTIES = {
  header: ['headerBgColor', 'logoUrl', 'logoWidth', 'logoHeight'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  // Outline pill CTA: the accent color paints both the border and the label, so a separate
  // text color would render nothing.
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor'],
  footer: ['socialMediaLinks', 'hotelPhone', 'hotelWebsite', 'hotelWebsiteUrl']
}

const WELCOME_SUBSCRIPTION_SECTION_PROPERTIES = {
  header: ['headerBgColor', 'logoUrl', 'logoWidth', 'logoHeight', 'headerTaglineScript'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor', 'ctaTextColor'],
  footer: ['socialMediaLinks', 'hotelPhone', 'hotelWebsite', 'hotelWebsiteUrl']
}

const DESTINATION_SHOWCASE_SECTION_PROPERTIES = {
  header: ['logoUrl', 'logoWidth', 'logoHeight', 'headerBgColor', 'socialMediaLinks'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  // Text-link CTA: it inherits the link color, so only the accent color applies.
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor'],
  col1: ['col1Image', 'col1ImageLinkUrl', 'col1Html', 'col1LinkText', 'col1LinkUrl'],
  col2: ['col2Image', 'col2ImageLinkUrl', 'col2Html', 'col2LinkText', 'col2LinkUrl'],
  discoverIntro: ['galleryTitle'],
  discoverButtons: [
    'discoverBtn1',
    'discoverBtn1LinkUrl',
    'discoverBtn2',
    'discoverBtn2LinkUrl',
    'discoverBtn3',
    'discoverBtn3LinkUrl',
    'discoverBtn4',
    'discoverBtn4LinkUrl'
  ],
  footer: ['socialMediaLinks', 'hotelPhone', 'hotelWebsite', 'hotelWebsiteUrl', 'finePrintHtml']
}

// The shared defaults in sectionsToProperties cover every layout variant ever built, so the
// simple shells have to narrow them down to the fields they really render.
const STANDARD_SHELL_SECTION_PROPERTIES = {
  header: ['headerBgColor', 'logoUrl', 'logoWidth', 'logoHeight'],
  hero: ['heroImgUrl', 'heroImageLinkUrl'],
  body: ['emailTitle', 'bodyHtml', 'ctaText', 'ctaBackgroundColor', 'ctaTextColor'],
  footer: ['socialMediaLinks', 'hotelPhone', 'hotelWebsite', 'hotelWebsiteUrl']
}

const TWO_COLUMN_SECTION_PROPERTIES = {
  ...STANDARD_SHELL_SECTION_PROPERTIES,
  col1: ['col1Image', 'col1ImageLinkUrl', 'col1Html'],
  col2: ['col2Image', 'col2ImageLinkUrl', 'col2Html']
}

const TEMPLATE_SECTION_PROPERTY_OVERRIDES = {
  default: STANDARD_SHELL_SECTION_PROPERTIES,
  centeredHero: STANDARD_SHELL_SECTION_PROPERTIES,
  twoColumn: TWO_COLUMN_SECTION_PROPERTIES,
  resortDiscovery: RESORT_DISCOVERY_SECTION_PROPERTIES,
  editorialEvent: EDITORIAL_EVENT_SECTION_PROPERTIES,
  cinematicConfirm: CINEMATIC_CONFIRM_SECTION_PROPERTIES,
  welcomeSubscription: WELCOME_SUBSCRIPTION_SECTION_PROPERTIES,
  destinationShowcase: DESTINATION_SHOWCASE_SECTION_PROPERTIES
}

const TEMPLATE_SECTION_NAME_OVERRIDES = {
  resortDiscovery: {
    lifestyle: 'Discover image',
    discoverIntro: 'Discover intro',
    discoverButtons: 'Discover buttons',
    gallery: 'Photo gallery'
  },
  editorialEvent: {
    col1: 'Experience 1',
    col2: 'Experience 2',
    col3: 'Experience 3',
    experiencesIntro: 'Experiences heading'
  },
  welcomeSubscription: {
    header: 'Logo & tagline'
  },
  destinationShowcase: {
    col1: 'Feature story 1',
    col2: 'Feature story 2',
    discoverIntro: 'Destinations heading',
    discoverButtons: 'Destination links'
  }
}

const TEMPLATE_PROPERTY_NAME_OVERRIDES = {
  resortDiscovery: {
    galleryTitle: 'Section heading',
    discoverIntroText: 'Section intro text'
  },
  editorialEvent: {
    featuresSectionTitle: 'Section heading',
    col1Html: 'Story content',
    col2Html: 'Story content',
    col3Html: 'Story content',
    col1LinkText: 'Button text',
    col2LinkText: 'Button text',
    col3LinkText: 'Button text',
    col1LinkUrl: 'Button URL',
    col2LinkUrl: 'Button URL',
    col3LinkUrl: 'Button URL',
    col1Image: 'Story image',
    col2Image: 'Story image',
    col3Image: 'Story image',
    ctaBackgroundColor: 'Button border color',
    ctaTextColor: 'Button text color',
    finePrintHtml: 'Footer legal text'
  },
  welcomeSubscription: {
    headerTaglineScript: 'Tagline'
  },
  destinationShowcase: {
    galleryTitle: 'Destinations heading',
    col1Html: 'Feature content',
    col2Html: 'Feature content',
    col1LinkText: 'Link text',
    col2LinkText: 'Link text',
    discoverBtn1: 'Destination 1 label',
    discoverBtn2: 'Destination 2 label',
    discoverBtn3: 'Destination 3 label',
    discoverBtn4: 'Destination 4 label'
  }
}

export function getTemplateSectionProperties(templateKey, sectionKey) {
  const override = TEMPLATE_SECTION_PROPERTY_OVERRIDES[templateKey]?.[sectionKey]

  if (override) {
    return override
  }

  return sectionsToProperties[sectionKey] || []
}

export function getTemplateSectionLabel(templateKey, sectionKey) {
  return (
    TEMPLATE_SECTION_NAME_OVERRIDES[templateKey]?.[sectionKey] ||
    editableSectionNames[sectionKey] ||
    sectionKey
  )
}

export function getTemplatePropertyLabel(templateKey, propertyKey) {
  return (
    TEMPLATE_PROPERTY_NAME_OVERRIDES[templateKey]?.[propertyKey] ||
    propertyNames[propertyKey] ||
    propertyKey
  )
}

export default getTemplateSectionProperties
